import { useQuery } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";
import { toApiDate } from "@marekl/adminer/datetime";
import { apiGet } from "../api";
import { businessToday } from "../businessDay";
import type { VoucherType } from "./voucherTypes";
import type { RideMethod, VoucherSource } from "./vouchers";

export interface DashboardLatestRide {
  id: string;
  created_at: Dayjs;
  method: RideMethod;
  cancelled: boolean;
  voucher_id: string;
  voucher_barcode: string;
  customer_name: string;
  voucher_type?: VoucherType | null;
}

export interface TodaySummaryResponse {
  rides_today: number;
  customers_today: number;
  vouchers_sold_today: number;
  certificates_ordered_today: number;
  latest_rides: DashboardLatestRide[];
}

export function useTodaySummary() {
  return useQuery<TodaySummaryResponse>({
    queryKey: ["today-summary"],
    queryFn: () => apiGet<TodaySummaryResponse>("/reports/today-summary"),
    refetchInterval: 30_000,
  });
}

// ---- Daily overview (reception) ----

export interface DailySoldVoucher {
  id: string;
  created_at: Dayjs;
  barcode: string;
  customer_name: string;
  source: VoucherSource;
  sale_price_czk: number;
  voucher_type?: VoucherType | null;
}

export interface DailyOverviewResponse {
  rides_today: number;
  customers_today: number;
  vouchers_sold_today: number;
  certificates_ordered_today: number;
  rides: DashboardLatestRide[];
  vouchers_sold: DailySoldVoucher[];
}

export function useDailyOverview() {
  // Key by the current business day so crossing the work-day boundary (e.g. a
  // session left open past midnight / the shift hour) invalidates yesterday's
  // cached data immediately instead of waiting for the 30s refetch interval.
  return useQuery<DailyOverviewResponse>({
    queryKey: ["daily-overview", toApiDate(businessToday())],
    queryFn: () => apiGet<DailyOverviewResponse>("/reports/daily-overview"),
    refetchInterval: 30_000,
  });
}

// ---- Aggregate report ----

export interface ReportTotals {
  rides: number;
  customers: number;
  vouchers_issued: number;
  counter_vouchers: number;
  eshop_vouchers: number;
}

export interface DailyBucket {
  day: Dayjs; // auto-converted from ISO datetime by api.ts
  rides: number;
  customers: number;
  vouchers_issued: number;
}

export interface TypeBreakdown {
  type_id: number;
  code: string;
  name: string;
  rides: number;
  customers: number;
  vouchers: number;
}

export interface ReportAggregateParams {
  from: string; // YYYY-MM-DD
  to: string;
  type?: number;
  source?: string;
  granularity?: "daily" | "hourly";
}

export interface ReportAggregateResponse {
  from: string;
  to: string;
  granularity: "daily" | "hourly";
  totals: ReportTotals;
  series: DailyBucket[];
  breakdown: TypeBreakdown[];
}

export function useReportAggregate(params: ReportAggregateParams, enabled = true) {
  return useQuery<ReportAggregateResponse>({
    queryKey: ["report-aggregate", params],
    queryFn: () => {
      const sp = new URLSearchParams({ from: params.from, to: params.to });
      if (params.type) sp.set("type", String(params.type));
      if (params.source) sp.set("source", params.source);
      if (params.granularity) sp.set("granularity", params.granularity);
      return apiGet<ReportAggregateResponse>(`/reports/aggregate?${sp}`);
    },
    enabled,
  });
}

// ---- Rides detail report ----

export interface RideDetailItem {
  ride_id: string;
  ride_created_at: Dayjs; // auto-converted
  cancelled: boolean;
  voucher_id: string;
  voucher_barcode: string;
  customer_name: string;
  voucher_type_code: string;
  voucher_type_name: string;
  user_id?: number;
  username: string;
  display_name: string;
}

export interface ReportRidesParams {
  from: string;
  to: string;
  type?: number;
  source?: string;
  page?: number;
  per_page?: number;
}

export interface ReportRidesResponse {
  items: RideDetailItem[];
  total: number;
  page: number;
  per_page: number;
}

export function useReportRides(params: ReportRidesParams, enabled = true) {
  return useQuery<ReportRidesResponse>({
    queryKey: ["report-rides", params],
    queryFn: () => {
      const sp = new URLSearchParams({ from: params.from, to: params.to });
      if (params.type) sp.set("type", String(params.type));
      if (params.source) sp.set("source", params.source);
      if (params.page && params.page > 1) sp.set("page", String(params.page));
      if (params.per_page) sp.set("per_page", String(params.per_page));
      return apiGet<ReportRidesResponse>(`/reports/rides?${sp}`);
    },
    enabled,
  });
}

// ---- Vouchers-issued report ----

export interface TypeIssuedRow {
  type_id: number;
  code: string;
  name: string;
  counter: number;
  eshop: number;
  total: number;
}

export interface ReportVouchersIssuedParams {
  from: string;
  to: string;
  type?: number;
  source?: string;
}

export interface ReportVouchersIssuedResponse {
  by_source: Record<string, number>;
  by_type: TypeIssuedRow[];
  total_counter: number;
  total_eshop: number;
  total_vouchers: number;
}

export function useReportVouchersIssued(params: ReportVouchersIssuedParams, enabled = true) {
  return useQuery<ReportVouchersIssuedResponse>({
    queryKey: ["report-vouchers-issued", params],
    queryFn: () => {
      const sp = new URLSearchParams({ from: params.from, to: params.to });
      if (params.type) sp.set("type", String(params.type));
      if (params.source) sp.set("source", params.source);
      return apiGet<ReportVouchersIssuedResponse>(`/reports/vouchers-issued?${sp}`);
    },
    enabled,
  });
}

// ---- Download helpers ----

type DownloadKind = "aggregate" | "rides" | "vouchers-issued";

async function fetchAndSaveBlob(url: string, filename: string): Promise<void> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    let msg = `Export selhal (${res.status})`;
    try {
      const body = await res.json();
      if (typeof body?.message === "string") msg = body.message;
    } catch {
      // ignore parse errors
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const objURL = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objURL;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objURL);
}

function apiBase(): string {
  return (window as Window & { __KARTIS_CONFIG__?: { apiBaseURL: string } }).__KARTIS_CONFIG__?.apiBaseURL ?? "/api";
}

export async function downloadReport(
  format: "csv",
  kind: DownloadKind,
  params: { from: string; to: string; type?: number; source?: string },
): Promise<void> {
  const sp = new URLSearchParams({ from: params.from, to: params.to });
  if (params.type) sp.set("type", String(params.type));
  if (params.source) sp.set("source", params.source);
  const url = `${apiBase()}/reports/${kind}.${format}?${sp}`;
  await fetchAndSaveBlob(url, `${kind}-${params.from}-${params.to}.${format}`);
}

/** Download the Denní report (daily cash-register tally sheet) PDF for the given date (YYYY-MM-DD). */
export async function downloadDailyReport(date: string): Promise<void> {
  const sp = new URLSearchParams({ date });
  const url = `${apiBase()}/reports/daily.pdf?${sp}`;
  await fetchAndSaveBlob(url, `denniReport-${date}.pdf`);
}

/**
 * Fetch the Denní report PDF for the given date and open the browser's print
 * dialog for it directly, via a hidden same-origin iframe. Blob object URLs
 * inherit the creating document's origin, so we can call print() on the iframe
 * window — this pops the print dialog without a detour through a separate tab.
 */
export async function printDailyReport(date: string): Promise<void> {
  const sp = new URLSearchParams({ date });
  const url = `${apiBase()}/reports/daily.pdf?${sp}`;
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    let msg = `Tisk selhal (${res.status})`;
    try {
      const body = await res.json();
      if (typeof body?.message === "string") msg = body.message;
    } catch { /* ignore */ }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const objURL = URL.createObjectURL(blob);

  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.src = objURL;

  const cleanup = () => {
    URL.revokeObjectURL(objURL);
    iframe.remove();
  };

  iframe.onload = () => {
    const win = iframe.contentWindow;
    if (!win) {
      cleanup();
      return;
    }
    win.focus();
    win.print();
    // afterprint fires when the dialog closes; the timeout is a fallback for
    // browsers where afterprint is unreliable inside iframes.
    win.addEventListener("afterprint", cleanup);
    setTimeout(cleanup, 60_000);
  };

  document.body.appendChild(iframe);
}
